import type { Instant, Uuid } from "../types.ts";

/**
 * Auditoria de alterações.
 *
 * Cada mutação grava uma linha em `activity_logs` **dentro da mesma
 * transação** que a mudança. Se o log falhar, a mudança não acontece — não
 * existe alteração sem rastro.
 */

export type ActivityAction =
  | "deliverable.created"
  | "deliverable.updated"
  | "deliverable.moved"
  | "deliverable.resized"
  | "deliverable.status_changed"
  | "deliverable.duplicated"
  | "deliverable.deleted"
  | "deliverable.occurrence_overridden"
  | "deliverable.occurrence_cancelled"
  | "deliverable.recurrence_created"
  | "deliverable.recurrence_updated"
  | "deliverable.recurrence_removed"
  | "attachment.added"
  | "attachment.removed"
  | "client.created"
  | "client.updated"
  | "client.deleted"
  | "project.created"
  | "project.updated"
  | "project.deleted"
  | "share_link.created"
  | "share_link.revoked"
  | "share_link.rotated"
  | "share_link.accessed"
  | "social_account.connected"
  | "social_account.disconnected"
  | "social_account.updated"
  | "social_post.created"
  | "social_post.updated"
  | "social_post.scheduled"
  | "social_post.published"
  | "social_post.cancelled";

export const ACTIVITY_LABELS: Record<ActivityAction, string> = {
  "deliverable.created": "criou a entrega",
  "deliverable.updated": "editou a entrega",
  "deliverable.moved": "reagendou a entrega",
  "deliverable.resized": "alterou o período da entrega",
  "deliverable.status_changed": "alterou o status",
  "deliverable.duplicated": "duplicou a entrega",
  "deliverable.deleted": "excluiu a entrega",
  "deliverable.occurrence_overridden": "alterou uma ocorrência da série",
  "deliverable.occurrence_cancelled": "cancelou uma ocorrência da série",
  "deliverable.recurrence_created": "criou uma série recorrente",
  "deliverable.recurrence_updated": "alterou a recorrência",
  "deliverable.recurrence_removed": "removeu a recorrência",
  "attachment.added": "anexou um arquivo",
  "attachment.removed": "removeu um anexo",
  "client.created": "cadastrou o cliente",
  "client.updated": "editou o cliente",
  "client.deleted": "excluiu o cliente",
  "project.created": "criou o projeto",
  "project.updated": "editou o projeto",
  "project.deleted": "excluiu o projeto",
  "share_link.created": "gerou um link de acompanhamento",
  "share_link.revoked": "revogou um link de acompanhamento",
  "share_link.rotated": "renovou um link de acompanhamento",
  "share_link.accessed": "acessou o portal",
  "social_account.connected": "conectou uma rede social",
  "social_account.disconnected": "desconectou uma rede social",
  "social_account.updated": "atualizou uma rede social",
  "social_post.created": "criou uma publicação",
  "social_post.updated": "editou uma publicação",
  "social_post.scheduled": "agendou uma publicação",
  "social_post.published": "publicou nas redes",
  "social_post.cancelled": "cancelou uma publicação",
};

export type EntityType =
  | "deliverable"
  | "deliverable_occurrence"
  | "client"
  | "project"
  | "attachment"
  | "share_link"
  | "social_account"
  | "social_post";

export interface ActivityLogEntry {
  id: Uuid;
  organizationId: Uuid;
  actorUserId: Uuid | null;
  /** Preenchido quando a ação vem de um acesso público (só `share_link.accessed`). */
  actorShareLinkId: Uuid | null;
  action: ActivityAction;
  entityType: EntityType;
  entityId: Uuid;
  clientId: Uuid | null;
  projectId: Uuid | null;
  /** Diff campo → { de, para }. Nunca contém token, senha ou hash. */
  changes: Record<string, { from: unknown; to: unknown }> | null;
  metadata: Record<string, unknown> | null;
  createdAt: Instant;
}

const REDACTED_FIELDS = new Set([
  "tokenHash",
  "token",
  "passwordHash",
  "password",
  "secret",
  "token_hash",
  "password_hash",
]);

/**
 * Diff entre dois estados, apenas dos campos que mudaram.
 *
 * Campos sensíveis são removidos, não mascarados: registrar "passwordHash
 * mudou" já é informação suficiente para auditoria, sem persistir o valor.
 */
export function diffChanges<T extends Record<string, unknown>>(
  before: T,
  after: Partial<T>,
): Record<string, { from: unknown; to: unknown }> | null {
  const changes: Record<string, { from: unknown; to: unknown }> = {};

  for (const [key, next] of Object.entries(after)) {
    if (REDACTED_FIELDS.has(key)) continue;
    if (next === undefined) continue;

    const previous = before[key];
    if (isEqual(previous, next)) continue;

    changes[key] = { from: normalize(previous), to: normalize(next) };
  }

  return Object.keys(changes).length > 0 ? changes : null;
}

function normalize(value: unknown): unknown {
  if (value instanceof Date) return value.toISOString();
  return value;
}

function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (a instanceof Date || b instanceof Date) {
    const aTime = a instanceof Date ? a.getTime() : new Date(String(a)).getTime();
    const bTime = b instanceof Date ? b.getTime() : new Date(String(b)).getTime();
    return aTime === bTime;
  }
  if (a === null || b === null || a === undefined || b === undefined) return false;
  if (typeof a === "object" && typeof b === "object") {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

/** Frase pronta para a linha do histórico na UI. */
export function describeActivity(entry: {
  action: ActivityAction;
  actorName: string | null;
  changes: Record<string, { from: unknown; to: unknown }> | null;
}): string {
  const actor = entry.actorName ?? "Alguém";
  const verb = ACTIVITY_LABELS[entry.action];
  const fieldCount = entry.changes === null ? 0 : Object.keys(entry.changes).length;

  if (entry.action === "deliverable.updated" && fieldCount > 0) {
    const fields = Object.keys(entry.changes ?? {}).join(", ");
    return `${actor} ${verb} (${fields})`;
  }
  return `${actor} ${verb}`;
}
